/**
 * Nutrition calculations using the Mifflin-St Jeor equation (most accurate for BMR).
 * Activity multipliers from the Harris-Benedict revised scale.
 */

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2, // little or no exercise
  light: 1.375, // light exercise 1-3 days/week
  moderate: 1.55, // moderate exercise 3-5 days/week
  active: 1.725, // hard exercise 6-7 days/week
  veryActive: 1.9, // very hard exercise + physical job
};

const GOAL_ADJUSTMENTS = {
  lose: -0.2, // 20% caloric deficit
  maintain: 0,
  gain: 0.15, // 15% caloric surplus
};

/**
 * Mifflin-St Jeor Equation
 * Males:   10 × weight(kg) + 6.25 × height(cm) − 5 × age + 5
 * Females: 10 × weight(kg) + 6.25 × height(cm) − 5 × age − 161
 */
export const calculateBMR = (weight, height, age, gender) => {
  const base = 10 * weight + 6.25 * height - 5 * age;
  return Math.round(gender === 'male' ? base + 5 : base - 161);
};

export const calculateTDEE = (bmr, activityLevel) => {
  const multiplier = ACTIVITY_MULTIPLIERS[activityLevel] || 1.2;
  return Math.round(bmr * multiplier);
};

export const calculateTargetCalories = (tdee, goal) => {
  const adjustment = GOAL_ADJUSTMENTS[goal] || 0;
  return Math.round(tdee * (1 + adjustment));
};

/**
 * Protein is set per kg of bodyweight, remaining calories split between carbs and fat.
 * - Lose:     1.5 g/kg protein, remaining: 40% carbs / 60% fat
 * - Maintain: 1.6 g/kg protein, remaining: 55% carbs / 45% fat
 * - Gain:     2.0 g/kg protein, remaining: 60% carbs / 40% fat
 */
const PROTEIN_PER_KG = {
  lose: 1.5,
  maintain: 1.6,
  gain: 2.0,
};

const REMAINING_SPLITS = {
  lose: { carbs: 0.65, fat: 0.35 },
  maintain: { carbs: 0.55, fat: 0.45 },
  gain: { carbs: 0.6, fat: 0.4 },
};

export const calculateMacros = (targetCalories, goal, weight) => {
  const proteinPerKg = PROTEIN_PER_KG[goal] || PROTEIN_PER_KG.maintain;
  const remainingSplit = REMAINING_SPLITS[goal] || REMAINING_SPLITS.maintain;

  const proteinGrams = Math.round(weight * proteinPerKg);
  const proteinCalories = proteinGrams * 4;
  const remainingCalories = targetCalories - proteinCalories;

  const carbsGrams = Math.round((remainingCalories * remainingSplit.carbs) / 4);
  const fatGrams = Math.round((remainingCalories * remainingSplit.fat) / 9);

  const proteinPct = Math.round((proteinCalories / targetCalories) * 100);
  const carbsPct = Math.round(((carbsGrams * 4) / targetCalories) * 100);
  const fatPct = Math.round(((fatGrams * 9) / targetCalories) * 100);

  return {
    protein: proteinGrams,
    carbs: carbsGrams,
    fat: fatGrams,
    proteinPct,
    carbsPct,
    fatPct,
  };
};

export const calculateAll = (weight, height, age, gender, activityLevel, goal) => {
  const bmr = calculateBMR(weight, height, age, gender);
  const tdee = calculateTDEE(bmr, activityLevel);
  const targetCalories = calculateTargetCalories(tdee, goal);
  const macros = calculateMacros(targetCalories, goal, weight);

  return { bmr, tdee, targetCalories, macros };
};
